import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Leaf, X, Search, Plus, Minus, ChevronRight } from 'lucide-react';
import { FRESH_PRODUCE, searchFreshProduce, getAllFreshProduce, getSubcategoryDefs } from '../../../data/freshProduceData';
import { MealTimeSelector } from '../components/MealTimeSelector';

export function FreshProduceModal({
  isOpen,
  onClose,
  onAddFood,
  defaultMealTime = 'breakfast',
  mealTimes,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [subCategory, setSubCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [portionSize, setPortionSize] = useState('');
  const [selectedMealTime, setSelectedMealTime] = useState(defaultMealTime);

  // Get all produce items
  const allProduce = useMemo(() => {
    const all = getAllFreshProduce();
    const items = [];
    all.fruits.forEach(item => items.push(item));
    all.vegetables.forEach(item => items.push(item));
    all.herbs.forEach(item => items.push(item));
    return items;
  }, []);

  // Get subcategory options for current main category
  const subCategoryDefs = useMemo(() => {
    return getSubcategoryDefs(category);
  }, [category]);

  // Count items per subcategory
  const subCategoryCounts = useMemo(() => {
    const counts = {};
    const catMap = { fruits: 'Fruit', vegetables: 'Vegetable', herbs: 'Herb' };
    const mainCat = catMap[category];
    if (!mainCat) return counts;
    const catItems = allProduce.filter(i => i.category === mainCat);
    subCategoryDefs.forEach(def => {
      counts[def.id] = catItems.filter(i => i.sub === def.id).length;
    });
    return counts;
  }, [allProduce, category, subCategoryDefs]);

  // Filter produce based on search, category, and subcategory
  const filteredProduce = useMemo(() => {
    let items = allProduce;
    
    if (category !== 'all') {
      const catMap = { fruits: 'Fruit', vegetables: 'Vegetable', herbs: 'Herb' };
      items = items.filter(item => item.category === catMap[category]);
    }
    
    if (subCategory !== 'all') {
      items = items.filter(item => item.sub === subCategory);
    }
    
    if (searchQuery.trim()) {
      const search = searchQuery.toLowerCase();
      items = items.filter(item => item.name.toLowerCase().includes(search));
    }
    
    return items;
  }, [allProduce, category, subCategory, searchQuery]);

  const handleAddItem = () => {
    if (!selectedItem) return;
    
    const portion = parseInt(portionSize) || 0;
    const multiplier = portion / 100;
    const adjustedItem = {
      ...selectedItem,
      name: `${selectedItem.name} (${portion}g)`,
      calories: Math.round(selectedItem.calories * multiplier),
      protein: Math.round(selectedItem.protein * multiplier * 10) / 10,
      carbs: Math.round(selectedItem.carbs * multiplier * 10) / 10,
      fat: Math.round(selectedItem.fat * multiplier * 10) / 10,
      grams: portion,
      mealTime: selectedMealTime,
    };
    
    onAddFood(adjustedItem);
    setSelectedItem(null);
    setPortionSize('');
  };

  const handleClose = () => {
    setSearchQuery('');
    setCategory('all');
    setSubCategory('all');
    setSelectedItem(null);
    setPortionSize('');
    onClose();
  };

  if (!isOpen) return null;

  // Portion size modal
  if (selectedItem) {
    const multiplier = (parseInt(portionSize) || 0) / 100;
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-slate-700 w-full max-w-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center justify-between">
              <span className="text-lg">{selectedItem.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedItem(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Portion Size Input */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Portion Size (grams)</label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPortionSize(Math.max(10, (parseInt(portionSize) || 0) - 10))}
                  className="border-slate-600 text-slate-300"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  value={portionSize}
                  onChange={(e) => setPortionSize(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white text-center w-24"
                  placeholder="g"
                  min="1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPortionSize((parseInt(portionSize) || 0) + 10)}
                  className="border-slate-600 text-slate-300"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {/* Quick presets */}
              <div className="flex gap-2 mt-2">
                {[50, 100, 150, 200].map(size => (
                  <button
                    key={size}
                    onClick={() => setPortionSize(size.toString())}
                    className={`px-3 py-1 rounded text-xs ${
                      parseInt(portionSize) === size 
                        ? 'bg-lime-500 text-white' 
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {size}g
                  </button>
                ))}
              </div>
            </div>
            
            {/* Nutrition Preview */}
            <div className="bg-slate-700/50 rounded-lg p-3">
              <p className="text-sm text-slate-400 mb-2">Nutrition{parseInt(portionSize) > 0 ? ` for ${parseInt(portionSize)}g` : ' per 100g'}:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-orange-400">
                  Calories: {Math.round(selectedItem.calories * multiplier)}
                </div>
                <div className="text-red-400">
                  Protein: {Math.round(selectedItem.protein * multiplier * 10) / 10}g
                </div>
                <div className="text-yellow-400">
                  Carbs: {Math.round(selectedItem.carbs * multiplier * 10) / 10}g
                </div>
                <div className="text-blue-400">
                  Fat: {Math.round(selectedItem.fat * multiplier * 10) / 10}g
                </div>
              </div>
            </div>

            {/* Meal Time Selection */}
            <div className="space-y-1">
              <label className="text-sm text-slate-400">Add to:</label>
              <MealTimeSelector 
                selectedMealTime={selectedMealTime} 
                onMealTimeChange={setSelectedMealTime}
                compact
                mealTimes={mealTimes}
              />
            </div>
            
            {/* Add Button */}
            <Button
              onClick={handleAddItem}
              className="w-full bg-lime-500 hover:bg-lime-400"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add {selectedItem.name}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="bg-slate-800 border-slate-700 w-full max-w-md my-8 max-h-[85vh] flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Leaf className="h-5 w-5 text-lime-400" />
              Fresh Produce
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="text-slate-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </Button>
          </CardTitle>
          <p className="text-xs text-slate-500">Raw fruits, vegetables & herbs (per 100g)</p>
        </CardHeader>
        
        <CardContent className="space-y-3 overflow-y-auto flex-1">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search fruits, vegetables..."
              className="bg-slate-700 border-slate-600 text-white pl-10"
              autoFocus
            />
          </div>
          
          {/* Category Filter */}
          <div className="flex gap-2">
            {[
              { id: 'all', label: 'All' },
              { id: 'fruits', label: 'Fruits' },
              { id: 'vegetables', label: 'Vegetables' },
              { id: 'herbs', label: 'Herbs' },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => { setCategory(cat.id); setSubCategory('all'); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  category === cat.id
                    ? 'bg-lime-500 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
                data-testid={`fresh-cat-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
          
          {/* Subcategory Filter - only show when a specific category is selected */}
          {subCategoryDefs.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="subcategory-filters">
              <button
                onClick={() => setSubCategory('all')}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  subCategory === 'all'
                    ? 'bg-slate-500 text-white'
                    : 'bg-slate-700/60 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                }`}
              >
                All
              </button>
              {subCategoryDefs.map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => setSubCategory(sub.id)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                    subCategory === sub.id
                      ? 'bg-cyan-600 text-white'
                      : 'bg-slate-700/60 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                  }`}
                  data-testid={`fresh-sub-${sub.id}`}
                >
                  {sub.label} <span className="opacity-60 ml-0.5">{subCategoryCounts[sub.id] || 0}</span>
                </button>
              ))}
            </div>
          )}
          
          {/* Results */}
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {filteredProduce.length > 0 && (
              <p className="text-[10px] text-slate-500 px-1">{filteredProduce.length} items</p>
            )}
            {filteredProduce.length > 0 ? (
              filteredProduce.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setSelectedItem(item);
                    setPortionSize('');
                  }}
                  className="w-full p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-left transition-colors"
                  data-testid={`fresh-item-${idx}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          item.category === 'Fruit' ? 'bg-red-500/20 text-red-300' :
                          item.category === 'Vegetable' ? 'bg-green-500/20 text-green-300' :
                          'bg-blue-500/20 text-blue-300'
                        }`}>
                          {item.category}
                        </span>
                        <span className="text-white font-medium text-sm">{item.name}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs">
                        <span className="text-orange-400">{item.calories} cal/100g</span>
                        <span className="text-red-400">P: {item.protein}g</span>
                        <span className="text-yellow-400">C: {item.carbs}g</span>
                        <span className="text-blue-400">F: {item.fat}g</span>
                      </div>
                      {item.servingNote && (
                        <p className="text-[10px] text-slate-500 mt-1">{item.servingNote}</p>
                      )}
                    </div>
                    <Plus className="h-5 w-5 text-lime-400 flex-shrink-0" />
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <Leaf className="h-12 w-12 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-400">No items found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default FreshProduceModal;
